import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Users, Phone, MapPin, FileText, FileSpreadsheet } from 'lucide-react';
import { fmtCurrency } from './helpers';

const AgentListView = ({
  agents, listLoading, searchTerm, setSearchTerm,
  selectAgent, downloadAllAgentsSummary,
}) => {
  const filteredAgents = agents.filter(a => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return a.name?.toLowerCase().includes(q) || a.phone?.includes(q) || a.city?.toLowerCase().includes(q);
  });

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="agent-commission-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Agent Commission</h1>
          <p className="text-gray-600 mt-1">Track and manage agent commissions on Net Sales</p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadAllAgentsSummary('excel')} data-testid="download-all-agents-excel">
            <FileSpreadsheet className="w-4 h-4" /> All Agents Excel
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-red-700 border-red-200 hover:bg-red-50" onClick={() => downloadAllAgentsSummary('pdf')} data-testid="download-all-agents-pdf">
            <FileText className="w-4 h-4" /> All Agents PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <Input placeholder="Search by name, phone, city..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} className="max-w-sm" data-testid="agent-search-input" />
        </CardContent>
      </Card>

      {listLoading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>
      ) : filteredAgents.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          {agents.length === 0 ? 'No agents found' : 'No matching agents'}
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="agent-list-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium">Agent Name</th>
                    <th className="px-3 py-3 text-left font-medium">Phone</th>
                    <th className="px-3 py-3 text-left font-medium">Area / City</th>
                    <th className="px-3 py-3 text-center font-medium">Customers</th>
                    <th className="px-3 py-3 text-center font-medium">Distributors</th>
                    <th className="px-3 py-3 text-center font-medium">Comm %</th>
                    <th className="px-3 py-3 text-right font-medium">Total Commission</th>
                    <th className="px-3 py-3 text-right font-medium">Paid</th>
                    <th className="px-3 py-3 text-right font-medium text-orange-700">Balance Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.map(ag => (
                    <tr key={ag.id} className="border-b border-gray-100 hover:bg-indigo-50 cursor-pointer transition-colors"
                      onClick={() => selectAgent(ag)} data-testid={`agent-row-${ag.id}`}>
                      <td className="px-4 py-3 font-semibold text-gray-800">{ag.name}</td>
                      <td className="px-3 py-3 text-gray-600 text-xs">
                        {ag.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{ag.phone}</span>}
                      </td>
                      <td className="px-3 py-3 text-gray-500 text-xs">
                        {(ag.area || ag.city) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[ag.area, ag.city].filter(Boolean).join(', ')}</span>}
                      </td>
                      <td className="px-3 py-3 text-center"><Badge className="bg-blue-100 text-blue-700 text-xs">{ag.customer_count}</Badge></td>
                      <td className="px-3 py-3 text-center"><Badge className="bg-purple-100 text-purple-700 text-xs">{ag.distributor_count}</Badge></td>
                      <td className="px-3 py-3 text-center text-xs font-medium">{ag.commission_percentage || 0}%</td>
                      <td className="px-3 py-3 text-right font-medium text-indigo-700">{fmtCurrency(ag.total_commission)}</td>
                      <td className="px-3 py-3 text-right font-medium text-green-700">{fmtCurrency(ag.total_paid)}</td>
                      <td className="px-3 py-3 text-right font-bold text-orange-700">{fmtCurrency(ag.balance_commission)}</td>
                    </tr>
                  ))}
                </tbody>
                {filteredAgents.length > 1 && (
                  <tfoot>
                    <tr className="bg-gray-100 border-t-2 border-gray-300">
                      <td colSpan={6} className="px-4 py-3 text-right font-bold text-gray-700">Total</td>
                      <td className="px-3 py-3 text-right font-bold text-indigo-700">{fmtCurrency(filteredAgents.reduce((s, a) => s + (a.total_commission || 0), 0))}</td>
                      <td className="px-3 py-3 text-right font-bold text-green-700">{fmtCurrency(filteredAgents.reduce((s, a) => s + (a.total_paid || 0), 0))}</td>
                      <td className="px-3 py-3 text-right font-bold text-orange-700">{fmtCurrency(filteredAgents.reduce((s, a) => s + (a.balance_commission || 0), 0))}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgentListView;
